import { type ExecutionModeV1, ExecutionModeV1Schema } from "@adpilot/shared";
import { IconChevronDown } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { Radio, RadioGroup } from "@/components/ui/radio-group";

interface ReviewAiModeSelectorProps {
  value: ExecutionModeV1;
  disabled?: boolean;
  /** @deprecated Approval status no longer affects the mode selector. */
  requiresActionApproval?: boolean;
  /** Caller owns the immediate projection, serialized saves, rollback and error feedback. */
  optimistic?: boolean;
  onChange: (mode: ExecutionModeV1) => Promise<boolean>;
}

const modes: readonly ExecutionModeV1[] = ["recommendation", "semi-auto", "full-auto"];

export function ReviewAiModeSelector({
  value,
  disabled,
  optimistic = false,
  onChange,
}: ReviewAiModeSelectorProps) {
  const { t } = useTranslation("smartCreation");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const sequence = useRef(0);
  const pendingChoice = useRef<{ mode: ExecutionModeV1; sequence: number } | null>(null);
  const waiting = !optimistic && saving;

  async function select(raw: unknown) {
    const parsed = ExecutionModeV1Schema.safeParse(raw);
    if (!parsed.success || disabled || waiting) return;
    if (parsed.data === value || parsed.data === pendingChoice.current?.mode) {
      setOpen(false);
      return;
    }
    const currentSequence = ++sequence.current;
    pendingChoice.current = { mode: parsed.data, sequence: currentSequence };
    if (optimistic) setOpen(false);
    else setSaving(true);
    setFailed(false);
    try {
      const accepted = await onChange(parsed.data);
      if (sequence.current !== currentSequence || optimistic) return;
      if (accepted) setOpen(false);
      else setFailed(true);
    } catch {
      if (sequence.current === currentSequence && !optimistic) setFailed(true);
    } finally {
      if (pendingChoice.current?.sequence === currentSequence) pendingChoice.current = null;
      if (sequence.current === currentSequence && !optimistic) setSaving(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button size="sm" variant="outline" disabled={disabled || waiting} loading={waiting} />
        }
      >
        <span className="text-muted-foreground">{t("review.preview.ai.mode")}</span>
        <span>{t(`review.preview.ai.modeLabel.${value}`)}</span>
        <IconChevronDown data-icon="inline-end" />
      </PopoverTrigger>
      <PopoverPopup
        align="end"
        className="smart-creation-preview w-[337px] [&>[data-slot=popover-viewport]]:p-1.5"
      >
        <RadioGroup
          value={value}
          disabled={disabled || waiting}
          className="gap-0"
          onValueChange={(mode) => void select(mode)}
          aria-label={t("review.preview.ai.mode")}
        >
          {modes.map((mode) => (
            <Field
              key={mode}
              orientation="horizontal"
              className="items-start gap-2.5 rounded-lg p-2.5 hover:bg-subtle"
            >
              <Radio
                id={`preview-ai-mode-${mode}`}
                value={mode}
                className="mt-0.5 size-4 [&_[data-slot=radio-indicator]]:size-4 [&_[data-slot=radio-indicator]]:before:size-1.5"
              />
              <div className="flex flex-1 flex-col gap-0.5">
                <FieldLabel className="text-xs leading-5" htmlFor={`preview-ai-mode-${mode}`}>
                  {t(`review.preview.ai.modeLabel.${mode}`)}
                </FieldLabel>
                <FieldDescription className="text-[11px] leading-4">
                  {t(`review.preview.ai.modeDescription.${mode}`)}
                </FieldDescription>
              </div>
            </Field>
          ))}
        </RadioGroup>
        {failed ? (
          <p role="alert" className="px-2.5 py-2 text-xs text-destructive">
            {t("review.aiActions.saveFailed")}
          </p>
        ) : null}
      </PopoverPopup>
    </Popover>
  );
}
